import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/buttons';
import React, { PropsWithChildren, ReactNode, useState } from 'react';
import Image, { type ImageProps } from 'next/image';
import IconPlay from '@/assets/icons/heroIcons/Play-filled.svg';
import { mergeClasses } from '@/utils/style';
import { useInAppDetect } from '@/hooks/useInAppDetect';
import PageHeadline from '@/components/PageHeadline';
import { MDXRemote } from 'next-mdx-remote';
import { isMarkdown } from '@/utils/markdown';
import { PageBanner, PageBannerVariant } from '@/api/sanity/pageBanners';
import { VideoModal } from '@/components/modal';
import { Text } from '@/components/typography';
import { Link } from '@/components/links';
import IconCheck from '@/assets/icons/heroIcons/Circle-check.svg';
interface VariantProps {
  container: string;
  content: string;
  image: {
    gap: string;
    topPosition: string;
    bottomPosition: string;
    width: number;
    height: number;
  };
  bgCircle?: ReactNode;
}
function LinkComponent(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  if (href) {
    let t1;
    if ($[0] !== children || $[1] !== href) {
      t1 = <Link to={href} testId="bannerTextLink" analyticsParam="bannerTextLink">{children}</Link>;
      $[0] = children;
      $[1] = href;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  return children;
}
function getVariant(variant: PageBannerVariant): VariantProps {
  switch (variant) {
    case 'mascots':
      return {
        container: 'pb-0 md:pb-0 lg:pb-0 xl:pb-20 2xb:pt-24 4xl:pb-20',
        content: 'xl:w-[37.5rem] 4xl:w-[43.75rem]',
        image: {
          gap: 'gap-[4.5rem] md:gap-[4.5rem] lg:gap-20 xl:gap-8 4xl:gap-[8.25rem]',
          topPosition: '4xl:-mt-14',
          bottomPosition: '',
          width: 668,
          height: 552
        },
        bgCircle: <div className={mergeClasses('bgCircle absolute bottom-0 left-1/2 rotate-0 xl:top-0 xl:bottom-auto', 'h-80 w-80 md:h-[27rem] md:w-[27rem] xl:h-[74rem] xl:w-[74rem]', '-translate-x-1/2 translate-y-10 xl:translate-x-24 xl:-translate-y-28')} />
      };
    case 'products':
      return {
        container: 'pb-12 md:pb-16 lg:pb-20 xl:pb-20 2xb:pt-24 4xl:pb-20',
        content: 'xl:w-[38.75rem] 4xl:w-[41.75rem]',
        image: {
          gap: 'gap-12 md:gap-16 lg:gap-20 xl:gap-[6.25rem] 4xl:gap-[14.5rem]',
          topPosition: '4xl:-mt-14',
          bottomPosition: '',
          width: 668,
          height: 552
        },
        bgCircle: <div className="bgCircle absolute top-0 left-0 h-[37.5rem] w-[37.5rem] -translate-x-1/2 -translate-y-1/3 rotate-180 md:h-[37.5rem] md:w-[37.5rem] md:-translate-x-1/3 xl:h-[55rem] xl:w-[55rem]" />
      };
    case 'video':
      return {
        container: 'pb-16 md:pb-[4.5rem] lg:pb-20 xl:pb-[5.5rem] 2xb:pt-26 4xl:pb-[7.5rem]',
        content: 'xl:w-[35.5rem] 4xl:w-192',
        image: {
          gap: 'gap-16 md:gap-[4.5rem] lg:gap-20 4xl:gap-[8.25rem]',
          bottomPosition: '',
          topPosition: '',
          width: 668,
          height: 376
        },
        bgCircle: <div className={mergeClasses('bgCircle absolute right-0 rotate-0', '4xl:h-[63.5rem] 4xl:w-[63.5rem] h-[37.5rem] w-[37.5rem] md:h-[37.5rem] md:w-[37.5rem] xl:h-[55rem] xl:w-[55rem]', '4xl:bottom-0 4xl:rotate-0 bottom-0 xl:top-0 xl:rotate-180', '4xl:translate-x-1/3 4xl:translate-y-1/4 translate-x-2/3 translate-y-1/4 md:translate-x-1/4 xl:translate-x-1/2 xl:-translate-y-1/3')} />
      };
    case 'mockupMobile':
      return {
        container: 'pb-0 md:pb-0 lg:pb-0 xl:pb-16 2xb:pt-20 4xl:pb-14',
        content: 'xl:w-[41.75rem] 4xl:w-192',
        image: {
          gap: 'gap-10 md:gap-[4.5rem] lg:gap-20 4xl:gap-[8.25rem]',
          topPosition: 'xl:-mt-4 4xl:-mt-[5.5rem]',
          bottomPosition: '-mb-10 md:-mb-14 xl:mb-0',
          width: 668,
          height: 600
        },
        bgCircle: <div className={mergeClasses('bgCircle absolute top-0 left-0 rotate-180', 'h-[25rem] w-[25rem] lg:h-[55rem] lg:w-[55rem]', '4xl:-translate-x-1/4 -translate-x-1/2 -translate-y-1/2 md:-translate-x-1/4 md:-translate-y-1/4 lg:-translate-x-1/2')} />
      };
    case 'mockupDesktop':
      return {
        container: 'pb-4 md:pb-14 lg:pb-[4.5rem] xl:pb-20 4xl:pb-14',
        content: 'xl:w-[35.5rem] 4xl:w-192',
        image: {
          gap: 'gap-10 md:gap-14 lg:gap-[4.5rem] xl:gap-8',
          topPosition: '4xl:-mt-14',
          bottomPosition: '',
          width: 768,
          height: 536
        },
        bgCircle: <div className={mergeClasses('bgCircle absolute right-0 rotate-0', '4xl:h-[63.5rem] 4xl:w-[63.5rem] h-[37.5rem] w-[37.5rem] md:h-[37.5rem] md:w-[37.5rem] xl:h-[55rem] xl:w-[55rem]', 'bottom-0 xl:top-0 xl:rotate-180', 'translate-x-2/3 translate-y-1/4 md:translate-x-1/4 xl:translate-x-1/2 xl:-translate-y-1/3')} />
      };
    default:
      return {
        container: 'pb-0 md:pb-0 lg:pb-0 xl:pb-16 2xb:pt-20 4xl:pb-24',
        content: 'xl:w-[41.75rem] 4xl:w-192',
        image: {
          gap: 'gap-10 md:gap-[4.5rem] lg:gap-20 xl:gap-8 4xl:gap-[8.25rem]',
          topPosition: 'xl:-mt-4 4xl:-mt-12',
          bottomPosition: '-mb-10 md:-mb-14 xl:mb-0',
          width: 668,
          height: 628
        },
        bgCircle: <div className="bgCircle absolute top-0 left-0 h-[25rem] w-[25rem] -translate-x-1/2 -translate-y-1/3 rotate-180 md:h-[50rem] md:w-[50rem] md:-translate-x-1/3 xl:h-[55rem] xl:w-[55rem]" />
      };
  }
}
const defaultBlurData = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAmCAMAAACBKikvAAADAFBMVEUAAAAbgu0dgfAdgvAcgfAdge8REREeg/OIiosgIiYcge4dgvAcgfAcgvAhhPAbgfEdgvAREREcgPAODxAigekkhfAmhe8cgfMcbMYKDxMcctAjgecdd9YcZrgjfN7i6OtKle4hdtQjeNZ9fn/v8fSCiJvKy8+4ucAege9zeosdgvAVt4Te4eUdgvDn7fOIio3f4+jo7vTi5utkbH4dgvDj5+zX19nV1dcXsY7X19kWuodVi4eZnasFChAdkY8Xq48OExsnLTWZobEEChJ3kaO7vcQYgmv///8AAAAegvHo8v0WvIbx9PYlMET+/v8ZvYcZvYiprLn8/f0avYj39/f9/v4ZvIf5+fny9PT7+/vv8vTs7/H+pQAYvYcfgvDp7/X2+fvz9/oCAgLu8fIYtYLs8fcICAhmbnwYt4P5+/3w8PDw9Pjz8/MXsH7a3d/r7e8pKiooNEnf398EBQXl5ecYuYXp6utAQEAtLS3i4uQZu4dVVlzHys95f4vqQzUNDQ2ytb6usbnR2N00qFOUmKJChfQZGhp4kJzl7/vLzdOrrrq7vsafpK3frDO3uMCOkpv/2EbY2Ns0NDSAgIAgISGL3cM0xJVASluKn6rL1NqqrLTS0taGipT8ugva5/WrusKenqQECAyv0/nE2/WlqLWq5tKIvfc5kfKkpaywsbVbY3Lky7p7k58zPlBxd4Tf9e3xXlypqa6/vsDExMdQy6P+7tTF7eCiyfOo4M76cHErivFcZXW7yc5zsfXR5ftXofT3bJkPFBpra2vxuz2crbj5gK3KKDBKm/PeUG/wvct017fsfYjmYYPvyF7gZZ/2l7mcyPjExsxqcX/yRkznIkmqx83/4J9CTF/JO2cxDRfvhZz7VYnYPz9OV2fflS6pIiv91HlnqvXEkDD4OWrerVcmwI4lL0FQUFGc3sXWYzh8d3h5GyX3p6Dlhldk0K1IR0fWobH5yEUegtfav5iVXzu6hXd8gsBOgMqtdKsjPzoaJEBVsl56xo5Mq+ExYGE1jKQ3YfvvAAAAAXRSTlMAQObYZgAAAAFiS0dEAIgFHUgAAAFlSURBVDjLhdTBioMwEIBh3yQX6UlCGoh9l3rYQ+4pSBEET9489rgUvPgefYe+0c5MEutmkt3/UAS/TlCjVRVbsNNpevum6YRVvCdG1kd/XDLw0l6goJdn6JvDh68lDT9t284Qcy72iMFxFvYN1ffuUAaCGX3N2Oy5DBzve1orX9PwkejMgBnK42bbUhjZsGOQ68qhFgBve0jXdX1lJnrXUTcpJcFtY5IgGGstUpRFiPOsrzuDLEBcGVwNgfw6E3xxKGhla69YXRfhPcD66mUYyaFKYBxZhH5pGhmvO4HahIs5rP0H3GVdwx0yIg/9SIvLh1tplEo2morwc8vh4RiRhWZ/1v9C2j1d7CaFUMkehy0K2wwkPGEZLGy1IoyUtJRZqLUQwggs0AGPASY3hyBLpVCXYPq+IqS3Lp2XDESpMoFjMJ5JcuxDcVDOfY44rFQ89avMd7TiymU+UYcv5HyoKsN5LsAft0RIudMs7RsAAAAASUVORK5CYII=';
const sharedImageProps: Partial<ImageProps> = {
  priority: true,
  placeholder: 'blur',
  blurDataURL: defaultBlurData,
  layout: 'responsive',
  sizes: '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 700px'
};
function Hero(props) {
  const $ = _c(62);
  const {
    className,
    contentClassName,
    customButton,
    customImageContent,
    layoutClassName,
    mediaClassName,
    buttons,
    highlights,
    variant,
    image,
    text,
    title,
    videoId
  } = props;
  const [isVideoModalOpened, setIsVideoModalOpened] = useState(false);
  const isInApp = useInAppDetect();
  let selectedVariant;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== buttons || $[1] !== className || $[2] !== contentClassName || $[3] !== customButton || $[4] !== customImageContent || $[5] !== highlights || $[6] !== isInApp || $[7] !== layoutClassName || $[8] !== mediaClassName || $[9] !== text || $[10] !== title || $[11] !== variant) {
    selectedVariant = getVariant(variant);
    t3 = mergeClasses("relative bg-blue-50", "4xl:pt-64 px-4 pt-[7.5rem] md:pt-[9.5rem] lg:pt-40 xl:pt-[13.5rem] 2xl:pt-60", {
      "pt-12": isInApp
    }, selectedVariant.container, !customImageContent && selectedVariant.image.bottomPosition, className);
    let t6;
    if ($[19] !== className) {
      t6 = mergeClasses("absolute top-full right-0 left-0 h-[50rem] bg-blue-50", className);
      $[19] = className;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    if ($[21] !== t6) {
      t4 = <div className={t6} />;
      $[21] = t6;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    t5 = selectedVariant.bgCircle;
    t1 = mergeClasses("relative z-10 mx-auto flex flex-col justify-between xl:flex-row", "4xl:max-w-[98rem] md:max-w-[38.5rem] lg:max-w-192 xl:max-w-292", selectedVariant.image.gap, layoutClassName);
    let t7;
    if ($[23] !== title) {
      t7 = <PageHeadline className="mx-auto xl:mx-0" id="title" text={title} />;
      $[23] = title;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] !== text) {
      t8 = isMarkdown(text) ? <MDXRemote {...text} components={{
        a: LinkComponent,
        ul: _temp,
        li: _temp2
      }} /> : text;
      $[25] = text;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== t8) {
      t9 = <Text as="div" variant="p1" className="mx-auto text-center text-gray-800 xl:mx-0 xl:text-left">{t8}</Text>;
      $[27] = t8;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== t7 || $[30] !== t9) {
      t10 = <div className="order-1 text-center">{t7}{t9}</div>;
      $[29] = t7;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] !== buttons || $[33] !== customButton) {
      t11 = (buttons || customButton) && <div className="4xl:mt-16 order-3 mt-10 flex flex-col gap-2 md:order-2 lg:mt-14 lg:flex-row lg:justify-center lg:gap-4 xl:mt-14 xl:justify-start">{buttons && buttons.map(_temp3)}{customButton}</div>;
      $[32] = buttons;
      $[33] = customButton;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== highlights) {
      t12 = highlights && <div className={mergeClasses("order-2 mx-auto flex w-full max-w-[22rem] justify-between md:order-3 md:max-w-[29.5rem] md:gap-12 lg:max-w-[36.125rem] xl:max-w-full xl:justify-start xl:gap-16", "4xl:mt-24 mt-8 md:mt-10 lg:mt-14 xl:mt-[5.5rem]")}>{highlights.map(_temp4)}</div>;
      $[35] = highlights;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    t2 = <div className={mergeClasses("flex flex-col gap-0 xl:shrink-0", selectedVariant.content, contentClassName)}>{t10}{t11}{t12}</div>;
    t0 = mergeClasses("relative order-2 max-w-full xl:min-w-0 xl:flex-1", !customImageContent && selectedVariant.image.topPosition, mediaClassName);
    $[0] = buttons;
    $[1] = className;
    $[2] = contentClassName;
    $[3] = customButton;
    $[4] = customImageContent;
    $[5] = highlights;
    $[6] = isInApp;
    $[7] = layoutClassName;
    $[8] = mediaClassName;
    $[9] = text;
    $[10] = title;
    $[11] = variant;
    $[12] = selectedVariant;
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    selectedVariant = $[12];
    t0 = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
  }
  let t6;
  if ($[37] !== customImageContent || $[38] !== image || $[39] !== selectedVariant || $[40] !== variant) {
    t6 = !customImageContent && image && (variant !== "video" ? <Image {...sharedImageProps} src={image.url} alt={image.alt} width={selectedVariant.image.width} height={selectedVariant.image.height} className="mx-auto xl:mx-0" layout="responsive" fetchPriority="high" /> : <div className="shadow-card relative mx-auto max-w-[41.75rem] overflow-hidden rounded-2xl border-[6px] border-white lg:border-[0.5rem] xl:mx-0"><div className="absolute top-0 left-0 h-full w-full bg-gray-900 opacity-20" /><Image {...sharedImageProps} src={image.url} alt={image.alt} width={selectedVariant.image.width} height={selectedVariant.image.height} fetchPriority="high" /></div>);
    $[37] = customImageContent;
    $[38] = image;
    $[39] = selectedVariant;
    $[40] = variant;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] !== customImageContent || $[43] !== isVideoModalOpened || $[44] !== videoId) {
    t7 = !customImageContent && videoId && <><VideoModal videoId={videoId} isOpened={isVideoModalOpened} handleClose={() => setIsVideoModalOpened(false)} autoplay={true} /><button onClick={() => setIsVideoModalOpened(true)} type="button" data-test-id="btnHeroPlayVideo" data-analytics-param="btnHeroPlayVideo" aria-label={"P\u0159ehrat video"} className="animate-play-icon-button-glow 4xl:h-40 4xl:w-40 absolute top-1/2 left-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white transition hover:animate-none md:h-30 md:w-30"><IconPlay className="6 ml-[5%] h-auto w-[30%] text-blue-500" /></button></>;
    $[42] = customImageContent;
    $[43] = isVideoModalOpened;
    $[44] = videoId;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== customImageContent || $[47] !== t6 || $[48] !== t7) {
    t8 = <div className="relative h-auto">{customImageContent}{t6}{t7}</div>;
    $[46] = customImageContent;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] !== t0 || $[51] !== t8) {
    t9 = <div className={t0}>{t8}</div>;
    $[50] = t0;
    $[51] = t8;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== t1 || $[54] !== t2 || $[55] !== t9) {
    t10 = <div className={t1}>{t2}{t9}</div>;
    $[53] = t1;
    $[54] = t2;
    $[55] = t9;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== t10 || $[58] !== t3 || $[59] !== t4 || $[60] !== t5) {
    t11 = <div className={t3}>{t4}{t5}{t10}</div>;
    $[57] = t10;
    $[58] = t3;
    $[59] = t4;
    $[60] = t5;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  return t11;
}
function _temp4(highlight) {
  return <div key={highlight.title}><p className="text-center text-xl font-bold text-gray-900 min-[375px]:text-2xl lg:text-4xl xl:text-left">{highlight.title}</p><p className="text-center text-xs text-gray-800 min-[375px]:text-sm lg:text-left lg:text-xl">{highlight.text}</p></div>;
}
function _temp3(button) {
  return <Button key={button.label} {...button} className={mergeClasses("4xl:w-86 mx-0 max-lg:mx-auto max-lg:block lg:w-70")} align="center" size="lg" variant={button.variant === "primary" ? "primary" : "secondary"}>{button.label}</Button>;
}
function _temp2(t0) {
  const {
    children: children_0,
    ...props_1
  } = t0;
  return <li {...props_1} className="flex flex-row items-center gap-3"><IconCheck className="h-6 w-6 shrink-0" /><span>{children_0}</span></li>;
}
function _temp(t0) {
  const {
    children,
    ...props_0
  } = t0;
  return <ul {...props_0} className="my-4 flex flex-col gap-3">{children}</ul>;
}
export default Hero;